export const dpaEn = `# Data Processing Agreement: andrum Practice

**Version:** \`practice_dpa_v1\`  
**Effective:** 2026-07-29  
**Language:** English convenience translation. Swedish text in \`practice-dpa-sv.md\` prevails in case of conflict.

This data processing agreement covers processing of personal data under GDPR Article 28 when the Customer uses andrum Practice. When the Customer accepts the agreement in the Practice Console, version and timestamp are stored.

---

## 1. Parties

**Controller (“Customer” / “Practice”)**  
The psychologist, therapist, clinic or legal entity that creates a Practice account and invites clients.

**Processor (“andrum”)**  
The provider of andrum Practice under the andrum brand, including \`practice.andrum.app\` and related APIs.

---

## 2. Background

1. Customer uses andrum Practice to invite clients, assign between-session micro-experiments and view consent-scoped activity.  
2. andrum hosts the platform and processes personal data **on Customer’s behalf** for Practice links, assignments and clinician use.  
3. This DPA forms part of the andrum Practice terms when accepted in-product or signed separately.  
4. For the client’s own B2C andrum account outside shared Practice scope, andrum’s privacy policy applies; see Annex A.

---

## 3. Definitions

GDPR terms apply. Additionally:

| Term | Meaning |
|------|---------|
| **Service** | andrum Practice Console, API and related features |
| **Client** | Natural person linked to a practice via invite and consent |
| **Instructions** | This DPA, documented product behaviour and written Customer instructions |

---

## 4. Subject matter (Art. 28.3)

| | |
|--|--|
| **Purpose** | Enable homework, overview and communication between sessions in a non-clinical wellbeing frame |
| **Nature** | Hosting, storage, transmission, display, aggregation, email/push, AI summary of **shared** data only |
| **Duration** | Term of the agreement + required retention, then deletion/return under §12 |
| **Data subjects** | Linked clients; clinicians/members; invitee emails |
| **Data categories** | Annex B |

**Out of default scope:** diagnosis, replacing clinical records, automated clinical risk scoring, special-category data beyond voluntary share_scope.

---

## 5. Roles

1. **Customer is controller** for processing related to its professional practice.  
2. **andrum is processor** for processing performed for Customer in the Service.  
3. Clients give **separate consent** to sharing (\`practice_share\`). Without valid consent, andrum must not grant Customer access to new client data.  
4. andrum may process technical logs, security and billing as **controller** where required (privacy policy).

---

## 6. Processor obligations

andrum shall:

1. Process only on documented instructions (this DPA + product behaviour), unless EU/Member State law requires otherwise.  
2. Ensure authorised persons are under confidentiality.  
3. Implement appropriate TOMs (Annex C).  
4. Use sub-processors only under §8.  
5. Assist with data-subject requests relating to Service data, with reasonable fees if disproportionate.  
6. Assist with DPIA/prior consultation to a reasonable extent.  
7. Notify Customer of a personal data breach without undue delay, no later than **48 hours** after becoming aware, with known details.  
8. Delete or return personal data after end of services (§12) unless law requires storage.  
9. Make available information to demonstrate compliance and allow audits (§11).  
10. Not use client data to **train third-party foundation models** without separate written agreement; AI session brief uses shared data and ephemeral prompts only.

---

## 7. Controller obligations

Customer shall:

1. Have a lawful basis and valid instructions.  
2. Inform clients and ensure **consent** in the Service before sharing.  
3. Not use the Service as the **sole clinical record** or contrary to professional rules.  
4. Limit access to authorised staff; protect credentials.  
5. Avoid uploading unnecessary special-category data.  
6. Be responsible for **internal notes** and message content.  
7. Promptly notify andrum of suspected unauthorised account use.

---

## 8. Sub-processors

1. Customer grants **general authorisation** for sub-processors in Annex D and replacements in the same category, provided andrum:  
   - flows down equivalent data-protection terms, and  
   - informs Customer of material changes via email or trust page with reasonable notice (at least 14 days where practicable), with a right to object on reasonable grounds.  
2. andrum remains liable for sub-processors as for its own acts.

---

## 9. International transfers

1. Primary storage and processing in the **EU/EEA**.  
2. If transfers outside the EEA are required, andrum will use a valid mechanism (e.g. EU SCCs) and inform Customer where relevant.

---

## 10. Security

See Annex C: TLS, access control, multi-tenant isolation/RLS, audit of sensitive access, backups, rate limiting.

---

## 11. Audit

1. Customer may, at most **once per calendar year** (or upon suspected incident), request written compliance information and, if reasonably necessary, an audit on 30 days’ notice during business hours.  
2. Audits must not unreasonably disrupt operations or other customers’ data.  
3. Customer bears external audit costs unless material breaches by andrum are found.

---

## 12. Deletion and return

1. On **client disconnect**, Customer loses access to new data; history per product policy (default: no continued access).  
2. On **Practice account termination**: within **30 days** delete or anonymise practice-specific data, unless Customer requests export within 14 days of termination.  
3. Backups age out per normal cycle (typically ≤ 90 days).  
4. Client B2C accounts are not auto-deleted when a practice ends.

---

## 13. Liability

Per GDPR Art. 82 and the main terms, to the extent permitted by mandatory law.

---

## 14. Term

This DPA applies while andrum processes personal data for Customer under the Service; confidentiality, deletion and liability survive as applicable.

---

## 15. Governing law

Swedish law. Disputes in Swedish courts; Stockholm District Court as first instance for B2B unless mandatory law provides otherwise.

---

## 16. Acceptance

By checkbox when creating a practice, “Accept DPA” in Settings, or separate signature. **Stored version:** \`practice_dpa_v1\`.

---

## Annex A: B2C vs Practice roles (summary)

| Data | Typical controller | andrum role |
|------|--------------------|-------------|
| Client’s own B2C check-ins | andrum + client (service terms) | Per privacy policy |
| Shared view to clinician | **Customer** for its use | **Processor** |
| Internal clinician notes | **Customer** | Processor (hosting) |
| Practice subscription billing | andrum | Controller for billing data |

---

## Annex B: Data categories

Display name, user id, email; check-in scales; experiment/program status; timestamps; consent version; clinician notes; messages; limited audit IP; push tokens if enabled. **Not default:** full coach chat, free-text reflections (unless opt-in).

---

## Annex C: Security (summary)

TLS; EU hosting (Supabase EU, Fly EU); least privilege, JWT, membership checks, RLS; client-view audit log; secrets isolation; incident runbooks.

---

## Annex D: Sub-processors

| Sub-processor | Purpose | Region (target) |
|---------------|---------|-----------------|
| Supabase | DB, auth | EU |
| Fly.io | API hosting | EU |
| Cloudflare | CDN, DNS, pages | Edge; origin EU |
| Amazon SES | Transactional email | eu-north-1 (target) |
| Stripe | Payments | EU/US with safeguards |
| xAI | AI coach / session brief | Per vendor terms; limited prompt data |

Current list: \`https://andrum.app/trust\`.

---

*andrum Practice DPA \`practice_dpa_v1\`, 2026-07-29*
`;
